import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'
import type { SfConfirm } from '@app/types'
import useI18n from '@app/shared/vue/composables/use-I18n'

// Connects to data-controller="fiscal-years"
export default class extends Controller {
  private checkTarget!: HTMLInputElement
  private checkTargets!: HTMLInputElement[]
  private inputTargets!: HTMLInputElement
  private inputsTargets!: HTMLInputElement[]
  private formTarget!: HTMLFormElement
  private saveButtonTarget!: HTMLInputElement
  private hasSaveButtonTarget!: boolean
  private previewUrlValue!: string
  private hasPreviewUrlValue!: boolean
  private hiddenYearTarget!: HTMLElement
  private hiddenYearTargets!: HTMLElement[]
  private retainedEarningsTarget!: HTMLInputElement
  private reactiveBookingsValue!: boolean

  static targets = [
    'rollforward',
    'check',
    'input',
    'form',
    'hiddenYear',
    'retainedEarnings',
    'saveButton',
  ]

  static values = {
    previewUrl: String,
    reactiveBookings: Boolean,
  }

  inputTargetConnected(element: HTMLInputElement): void {
    // the event is triggered by jQuery, not a native JS event sadly
    $(element).on('change', () => this.handleYearInputChange(element))
  }

  inputTargetDisconnected(element: HTMLElement): void {
    // the event is triggered by jQuery, not a native JS event sadly
    $(element).off('change')
  }

  retainedEarningsTargetConnected(element: HTMLInputElement): void {
    $(element).on('change', () => {
      this.handleYearInputChange(element)
    })
  }

  retainedEarningsTargetDisconnected(element: HTMLElement): void {
    // the event is triggered by jQuery, not a native JS event sadly
    $(element).off('change')
  }

  disableChecks(): void {
    for (const check of this.checkTargets) {
      if (!this.reactiveBookingsValue || check.dataset.currentLic === 'true') {
        check.checked = false
      }
    }
    this.handleYearInputChange()
  }

  toggleHiddenYears(): void {
    for (const row of this.hiddenYearTargets) {
      row.classList.toggle('tw-hidden')
    }
  }

  toggleRollforward(event: Event): void {
    event.preventDefault()
    const checkbox = event.target as HTMLInputElement
    const sfConfirm = window.sfConfirm as SfConfirm<() => void>
    const I18n = useI18n()

    const sideEffectsSuffix = this.reactiveBookingsValue ? '_reactive' : ''

    if (checkbox.checked) {
      sfConfirm({
        question: I18n.t('js.companies.periods.enable_manual_rollforward'),
        message: I18n.t(
          `js.companies.periods.enable_manual_rollforward_side_effects${sideEffectsSuffix}`
        ),
        onConfirm: () => {
          checkbox.checked = true
          this.handleYearInputChange()
        },
        confirmLabel: I18n.t('js.companies.periods.enable'),
        cancelLabel: I18n.t('js.companies.periods.no'),
        options: {
          confirm_class: 'sf-btn sf-btn-success',
          message_class: 'tw-bg-white',
          swap_buttons: true,
        },
      })
    } else {
      sfConfirm({
        question: I18n.t('js.companies.periods.disable_manual_rollforward'),
        message: I18n.t(
          `js.companies.periods.disable_manual_rollforward_side_effects${sideEffectsSuffix}`
        ),
        onConfirm: () => {
          checkbox.checked = false
          this.disableChecks()
        },
        confirmLabel: I18n.t('js.companies.periods.disable'),
        cancelLabel: I18n.t('js.companies.periods.no'),
        options: {
          confirm_class: 'sf-btn sf-btn-warning',
          message_class: 'tw-bg-white',
        },
      })
    }
  }

  checkboxChanged(_event: Event): void {
    this.handleYearInputChange()
  }

  handleYearInputChange(element?: HTMLInputElement): void {
    if (!this.hasPreviewUrlValue) {
      return
    }

    const formData = new FormData(this.formTarget)
    const fields = Array.from(formData, ([name, value]) => ({
      name,
      value,
    }))

    if (element) {
      const regexResult = element.name.match(
        /fiscal_years\]\[(\d+)\]\[(end_date|start_date)\]/
      )

      if (regexResult) {
        fields.push({
          name: 'company[modified_year][index]',
          value: regexResult[1],
        })
        fields.push({
          name: 'company[modified_year][type]',
          value: regexResult[2],
        })
      }
    }

    const filteredFields = fields.filter(({ name }) => name !== '_method')

    const loadingAnimation = document.querySelector('.loading-animation')
    const overlay = document.querySelector('.overlay') as HTMLElement

    if (overlay) {
      overlay.style.display = 'block'
    }

    if (loadingAnimation) {
      loadingAnimation.classList.add('busy')
    }

    void $.post(this.previewUrlValue, filteredFields).always(function () {
      if (overlay) {
        overlay.style.display = 'none'
      }

      if (loadingAnimation) {
        loadingAnimation.classList.remove('busy')
      }
    })
  }
}
